import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRefData } from '../../lib/refdata';
import type { BankAccount, CalcException, PayrollRun, PitReturn, SpfReturnPayload, ThirteenthMonthRun } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

function sessionList<T>(key: string): T[] {
  const raw = sessionStorage.getItem(key);
  return raw ? (JSON.parse(raw) as T[]) : [];
}

export function Runs() {
  const { persona } = useAuth();
  const { fy, votes } = useRefData();
  const isDpa = persona?.roles.includes('DPA_PAYROLL_ADMIN') ?? false;
  const isHr = persona?.roles.includes('MDA_HR_OFFICER') ?? false;

  const [runs, setRuns] = useState<PayrollRun[]>(() => sessionList('govfin.payrollRuns'));
  const [showCreate, setShowCreate] = useState(false);
  const [payTarget, setPayTarget] = useState<PayrollRun | null>(null);
  const [postTarget, setPostTarget] = useState<PayrollRun | null>(null);
  const [returnsTarget, setReturnsTarget] = useState<PayrollRun | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [thirteenth, setThirteenth] = useState<ThirteenthMonthRun[]>(() => sessionList('govfin.thirteenthMonthRuns'));
  const [showThirteenth, setShowThirteenth] = useState(false);

  const persistRuns = (items: PayrollRun[]) => {
    setRuns(items);
    sessionStorage.setItem('govfin.payrollRuns', JSON.stringify(items));
  };
  const persistThirteenth = (items: ThirteenthMonthRun[]) => {
    setThirteenth(items);
    sessionStorage.setItem('govfin.thirteenthMonthRuns', JSON.stringify(items));
  };
  const patchRun = (id: string, patch: Partial<PayrollRun>) => persistRuns(runs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const voteName = (id: string) => {
    const v = votes.find((x) => x.id === id);
    return v ? v.code : id;
  };

  const calculate = async (run: PayrollRun) => {
    setActionError(null);
    try {
      const result = await api.post<PayrollRun>(`/api/v1/payroll/runs/${run.id}/calculate`);
      patchRun(run.id, result);
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Calculation failed');
    }
  };

  const clearExceptions = async (run: PayrollRun) => {
    setActionError(null);
    try {
      const result = await api.post<PayrollRun>(`/api/v1/payroll/runs/${run.id}/clear-exceptions`);
      patchRun(run.id, result);
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to clear exceptions');
    }
  };

  const verify = async (run: PayrollRun) => {
    setActionError(null);
    try {
      const result = await api.post<PayrollRun>(`/api/v1/payroll/runs/${run.id}/verify`);
      patchRun(run.id, result);
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    }
  };

  const approve = async (run: PayrollRun) => {
    setActionError(null);
    try {
      const result = await api.post<PayrollRun>(`/api/v1/payroll/runs/${run.id}/approve`);
      patchRun(run.id, result);
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Payroll Runs"
        subtitle="Calculate → clear exceptions → MDA verify → DPA approve → pay → post (wages hit F11, SPF return files to P02)"
        actions={
          isDpa ? (
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setShowThirteenth(true)}>
                13th month
              </button>
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                + New run
              </button>
            </div>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <p className="mb-3 text-xs text-slate-400">
        There is no GET /runs listing endpoint yet — this app remembers the runs you create or act on in this browser tab.
      </p>

      {runs.length === 0 ? (
        <div className="card">
          <EmptyState title="No payroll runs yet" hint="Create a run for a period to begin the calculation lifecycle." />
        </div>
      ) : (
        <div className="space-y-4">
          {runs.map((r) => (
            <div key={r.id} className="card p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">{r.period}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Votes: {r.voteScope.map(voteName).join(', ')}
                    {r.batchRef ? ` · batch ${r.batchRef}` : ''}
                    {r.spfReturnRef ? ` · SPF return ${r.spfReturnRef}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {isDpa && (r.status === 'DRAFT' || r.status === 'CALCULATED') ? (
                    <button className="btn-secondary" onClick={() => calculate(r)}>
                      {r.status === 'DRAFT' ? 'Calculate' : 'Recalculate'}
                    </button>
                  ) : null}
                  {isDpa && r.status === 'CALCULATED' && (r.exceptions?.length ?? 0) === 0 ? (
                    <button className="btn-success" onClick={() => clearExceptions(r)}>
                      Clear exceptions
                    </button>
                  ) : null}
                  {isHr && r.status === 'EXCEPTIONS_CLEARED' ? (
                    <button className="btn-success" onClick={() => verify(r)}>
                      Verify (HR)
                    </button>
                  ) : null}
                  {isDpa && r.status === 'MDA_VERIFIED' ? (
                    <button className="btn-success" onClick={() => approve(r)}>
                      Approve (DPA)
                    </button>
                  ) : null}
                  {isDpa && r.status === 'DPA_APPROVED' ? (
                    <button className="btn-primary" onClick={() => setPayTarget(r)}>
                      Pay
                    </button>
                  ) : null}
                  {isDpa && r.status === 'PAID' ? (
                    <button className="btn-primary" onClick={() => setPostTarget(r)}>
                      Post → F11 & SPF
                    </button>
                  ) : null}
                  {r.status === 'POSTED' ? (
                    <button className="btn-secondary" onClick={() => setReturnsTarget(r)}>
                      View returns
                    </button>
                  ) : null}
                </div>
              </div>

              {r.totals ? (
                <div className="mt-3 grid grid-cols-3 gap-3 text-sm sm:grid-cols-6">
                  <Stat label="Employees" value={String(r.totals.employees)} />
                  <Stat label="Gross" value={scr(r.totals.gross)} />
                  <Stat label="PIT" value={scr(r.totals.pit)} />
                  <Stat label="SPF (5+5)" value={scr(r.totals.spfEmployee + r.totals.spfEmployer)} />
                  <Stat label="Other deductions" value={scr(r.totals.otherDeductions)} />
                  <Stat label="Net" value={scr(r.totals.net)} />
                </div>
              ) : null}

              {(r.exceptions?.length ?? 0) > 0 ? (
                <div className="mt-3">
                  <div className="field-label">Exceptions ({r.exceptions.length})</div>
                  {r.exceptions.map((e: CalcException, idx: number) => (
                    <div key={idx} className="mb-1 rounded-lg bg-rose-50 px-3 py-1.5 text-xs text-rose-700">
                      {e.nin} — {e.code.replaceAll('_', ' ')}: {e.detail}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {thirteenth.length > 0 ? (
        <div className="mt-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">13th-month runs (this session)</h2>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Year</th>
                  <th className="table-head text-right">Employees</th>
                  <th className="table-head text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {thirteenth.map((t) => (
                  <tr key={t.year}>
                    <td className="table-cell font-medium">{t.year}</td>
                    <td className="table-cell text-right">{t.totals.employees}</td>
                    <td className="table-cell text-right tabular-nums">{scr(t.totals.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {showCreate ? (
        <CreateRunModal
          votes={votes}
          onClose={() => setShowCreate(false)}
          onDone={(run) => {
            setShowCreate(false);
            persistRuns([run, ...runs]);
          }}
        />
      ) : null}
      {payTarget ? (
        <PayRunModal
          run={payTarget}
          onClose={() => setPayTarget(null)}
          onDone={(result) => {
            setPayTarget(null);
            patchRun(payTarget.id, result);
          }}
        />
      ) : null}
      {postTarget && fy ? (
        <PostRunModal
          run={postTarget}
          fyId={fy.id}
          onClose={() => setPostTarget(null)}
          onDone={(result) => {
            setPostTarget(null);
            patchRun(postTarget.id, result);
          }}
        />
      ) : null}
      {returnsTarget ? <ReturnsModal run={returnsTarget} onClose={() => setReturnsTarget(null)} /> : null}
      {showThirteenth ? (
        <ThirteenthModal
          onClose={() => setShowThirteenth(false)}
          onDone={(run) => {
            setShowThirteenth(false);
            persistThirteenth([run, ...thirteenth]);
          }}
        />
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="tabular-nums font-semibold text-slate-700">{value}</div>
    </div>
  );
}

function CreateRunModal({
  votes,
  onClose,
  onDone,
}: {
  votes: Array<{ id: string; code: string; name: string }>;
  onClose: () => void;
  onDone: (run: PayrollRun) => void;
}) {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [voteScope, setVoteScope] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const run = await api.post<PayrollRun>('/api/v1/payroll/runs', { period, voteScope: [...voteScope] });
      onDone(run);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create run');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New payroll run" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Period">
          <input className="field-input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </Field>
        <div>
          <div className="field-label">Vote scope</div>
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
            {votes.map((v) => (
              <label key={v.id} className="flex items-center gap-2 px-1 py-1 text-sm">
                <input
                  type="checkbox"
                  checked={voteScope.has(v.id)}
                  onChange={() => {
                    const next = new Set(voteScope);
                    if (next.has(v.id)) next.delete(v.id);
                    else next.add(v.id);
                    setVoteScope(next);
                  }}
                />
                {v.code} — {v.name}
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || voteScope.size === 0}>
            {busy ? 'Creating…' : 'Create run'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PayRunModal({ run, onClose, onDone }: { run: PayrollRun; onClose: () => void; onDone: (result: PayrollRun) => void }) {
  const accountsQ = useApiQuery<{ items: BankAccount[] }>('/api/v1/payments/accounts');
  const [valueDate, setValueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [debtorAccountId, setDebtorAccountId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<PayrollRun>(`/api/v1/payroll/runs/${run.id}/pay`, { valueDate, debtorAccountId });
      onDone(result);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Pay failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Pay run — ${run.period}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Net salaries build one F06 payment batch; garnishment balances advance with the money.</p>
        <Field label="Value date">
          <input className="field-input" type="date" value={valueDate} onChange={(e) => setValueDate(e.target.value)} />
        </Field>
        <Field label="Debit account (TSA)">
          <select className="field-input" value={debtorAccountId} onChange={(e) => setDebtorAccountId(e.target.value)}>
            <option value="">Select account…</option>
            {(accountsQ.data?.items ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.cbsRef})
              </option>
            ))}
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !debtorAccountId}>
            {busy ? 'Paying…' : 'Pay run'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PostRunModal({ run, fyId, onClose, onDone }: { run: PayrollRun; fyId: string; onClose: () => void; onDone: (result: PayrollRun) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<PayrollRun>(`/api/v1/payroll/runs/${run.id}/post`, { fiscalYearId: fyId });
      onDone(result);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Post failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Post run — ${run.period}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-sm text-slate-600">
          Posts the wage bill to F11 per budget line and files the SPF return to P02 — one action, three systems agree.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Posting…' : 'Post run'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReturnsModal({ run, onClose }: { run: PayrollRun; onClose: () => void }) {
  const spfQ = useApiQuery<SpfReturnPayload>(`/api/v1/payroll/runs/${run.id}/spf-return`);
  const pitQ = useApiQuery<PitReturn>(`/api/v1/payroll/runs/${run.id}/pit-return`);
  return (
    <Modal title={`Returns — ${run.period}`} onClose={onClose} wide>
      <div className="space-y-4">
        <div>
          <h3 className="mb-1 text-sm font-bold text-slate-700">SPF return</h3>
          {spfQ.loading ? (
            <Spinner />
          ) : spfQ.data ? (
            <p className="text-sm text-slate-600">
              {spfQ.data.lines.length} employee(s) · employee {scr(spfQ.data.totals.employeeAmt)} · employer {scr(spfQ.data.totals.employerAmt)} · gross{' '}
              {scr(spfQ.data.totals.grossWage)}
            </p>
          ) : null}
        </div>
        <div>
          <h3 className="mb-1 text-sm font-bold text-slate-700">PIT return</h3>
          {pitQ.loading ? (
            <Spinner />
          ) : pitQ.data ? (
            <p className="text-sm text-slate-600">
              {pitQ.data.lines.length} employee(s) · total PIT {scr(pitQ.data.totalPit)}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ThirteenthModal({ onClose, onDone }: { onClose: () => void; onDone: (run: ThirteenthMonthRun) => void }) {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const run = await api.post<ThirteenthMonthRun>(`/api/v1/payroll/thirteenth/${year}`);
      onDone(run);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to compute 13th month');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Compute 13th-month pay" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Year">
          <input className="field-input" type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Computing…' : 'Compute'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
